import { isPlainObject } from 'is-plain-object';
import * as ids from 'short-id';
import { omit } from 'underscore';
import { PodcastTemplateConfig } from 'redux/middleware/api/podcast-service';
import { ISlideshowItem } from 'types';
import merge from 'utils/deepmerge';
import { parseCSSFilter } from 'utils/dom';
import { isDynamicImage } from 'utils/embed/slideshow';
import measurement, { ViewportWidth } from 'utils/measurement';
import {
  Layer,
  Slide,
  SlideEffectText,
  SlideEffectTextState,
  SlideshowState,
} from '../types';
import { WithTypedKeyAsset } from './types';

export const calculateSlideBlurRadius = (
  slide: ISlideshowItem,
): ViewportWidth | undefined => {
  const cssFilter = parseCSSFilter(slide?.style?.filter);

  return cssFilter?.blur?.radius && cssFilter.blur.radius.includes('vw')
    ? new ViewportWidth(parseInt(cssFilter.blur.radius.replace('vw', ''), 10))
    : undefined;
};

function getSlideEffectText(slide: ISlideshowItem, slideId: string) {
  if (slide.imageEffect.effectType !== 'lottie') return undefined;
  return (slide.imageEffect.options.embeddedTexts ?? []).reduce<{
    [k: string]: SlideEffectText;
  }>((acc, text) => {
    const id = ids.generate();
    acc[id] = {
      ...text,
      id,
      slideId,
      textType: text.textType,
    };
    return acc;
  }, {});
}

/**
 *
 * @param layers an array of layer ids where the index of the layer in the array
 * corresponds to the layer's order.  a layer corresponds to a track in the
 * editor. slideshow and videoClips share the same track so the layers array passed
 * in here can be the output of the getVideoClips function
 */
export function getSlideshow(
  config: PodcastTemplateConfig,
  layers: Layer[],
): WithTypedKeyAsset<SlideshowState> & { text: SlideEffectTextState } {
  const slideshow = config?.slideshowInfo ?? [];

  return slideshow.reduce(
    (acc, slide, index) => {
      const id = ids.generate();

      const layer = layers[slide.layerId];

      if (layer?.type !== 'media') {
        throw new Error(
          `Expected slideshow asset to reference layer of type "media" but got ${layer?.type}`,
        );
      }

      const slideState: Slide = {
        // merging with an empty object is used to deep clone the image object since
        // parts of it are used in state.  this prevents accidentally changing
        // the source object
        ...merge(
          omit(slide, 'imageUrl', 'position'),
          {},
          { isMergeableObject: isPlainObject },
        ),
        id,
        blurRadius: calculateSlideBlurRadius(slide),
        borderRadius: slide?.style?.borderRadius
          ? measurement(slide.style.borderRadius)
          : undefined,
        imageSrc: slide.imageUrl,
        layerId: layer.id,
        originalSlideshowIndex: index,
        placement: {
          height: measurement(slide.style?.height ?? '100vh'),
          left: measurement(slide.position?.left ?? '0vw'),
          top: measurement(slide.position?.top ?? '0vh'),
          width: measurement(slide.style?.width ?? '100vw'),
        },
      };

      if (isDynamicImage(slide)) {
        acc.keyAssets.mainImage = [...acc.keyAssets.mainImage, id];
      }

      const texts = getSlideEffectText(slide, id);
      if (texts) {
        const textIds = Object.keys(texts);
        acc.text = { ...acc.text, ...texts };
        slideState.imageEffect.options.embeddedTexts = textIds;

        // iterate through the slide texts to see if any are key text assets
        textIds.forEach(textId => {
          const text = texts[textId];
          if (text.textType) {
            acc.keyAssets[text.textType] = textId;
          }
        });
      }

      acc.order.push(id);
      acc.data[id] = slideState;

      return acc;
    },
    {
      keyAssets: { mainImage: [] } as WithTypedKeyAsset<any>['keyAssets'],
      newImages: [],
      order: [],
      data: {},
      text: {},
    },
  );
}

export function getHasCubeEffect(config: PodcastTemplateConfig) {
  const slideshow = config.slideshowInfo ?? [];
  return slideshow.some(slide => slide.imageEffect.effect === 'rotatingCube');
}
